import { useState, useEffect } from 'react';
import { Link } from 'react-router-dom';
import CategoryDropdown from './CategoryDropdown';
import { categories } from '@/mocks/categories';
import { blogArticles } from '@/mocks/articles';

export default function Hero() {
  const [scrolled, setScrolled] = useState(false);
  const [mobileMenuOpen, setMobileMenuOpen] = useState(false);

  useEffect(() => {
    const handleScroll = () => {
      setScrolled(window.scrollY > 50);
    };
    window.addEventListener('scroll', handleScroll, { passive: true });
    return () => window.removeEventListener('scroll', handleScroll);
  }, []);

  useEffect(() => {
    if (mobileMenuOpen) {
      document.body.style.overflow = 'hidden';
    } else {
      document.body.style.overflow = '';
    }
    return () => { document.body.style.overflow = ''; };
  }, [mobileMenuOpen]);

  return (
    <header className="relative w-full min-h-screen flex flex-col">
      {/* Navbar */}
      <nav
        className={`fixed top-0 left-0 right-0 z-50 transition-all duration-300 ${
          scrolled ? 'bg-background-50 border-b border-background-200/70' : 'bg-transparent'
        }`}
      >
        <div className="max-w-[1280px] mx-auto px-4 md:px-6 flex items-center justify-between h-16 md:h-20">
          <div className="flex items-center gap-6">
            <a href="/" className="font-heading text-lg sm:text-xl font-semibold text-foreground-950 whitespace-nowrap">
              kakobuyfinds<span className="text-primary-500">.forum</span>
            </a>
            <div className="hidden md:flex items-center gap-6">
              <CategoryDropdown />
              <a
                href="https://kaspreadsheet.com"
                target="_blank"
                rel="noopener nofollow"
                className="text-sm text-foreground-600 hover:text-primary-500 transition-colors whitespace-nowrap"
              >
                kaspreadsheet.com
              </a>
            </div>
          </div>

          <div className="flex items-center gap-3">
            <a
              href="https://kaspreadsheet.com"
              target="_blank"
              rel="noopener nofollow"
              className="hidden md:inline-flex items-center gap-2 px-5 py-2.5 bg-background-50 border border-foreground-300 text-foreground-800 text-sm font-medium rounded-full hover:border-primary-400 hover:text-primary-600 transition-all duration-200 whitespace-nowrap cursor-pointer"
            >
              kakobuy sheet
              <i className="ri-arrow-right-up-line w-4 h-4 flex items-center justify-center"></i>
            </a>
            <a
              href="https://kaspreadsheet.com"
              target="_blank"
              rel="noopener nofollow"
              className="hidden sm:inline-flex items-center gap-2 px-5 py-2.5 bg-primary-500 text-background-50 text-sm font-semibold rounded-full hover:bg-primary-600 transition-all duration-200 whitespace-nowrap cursor-pointer"
            >
              buy it now
              <i className="ri-arrow-right-up-line w-4 h-4 flex items-center justify-center"></i>
            </a>
            {/* Mobile hamburger button – only visible below md, categories live inside the slide-out menu */}
            <button
              onClick={() => setMobileMenuOpen(true)}
              className="md:hidden inline-flex items-center justify-center w-10 h-10 rounded-xl bg-background-50/90 text-foreground-800 hover:bg-background-50 transition-colors cursor-pointer"
              aria-label="Otwórz menu"
            >
              <i className="ri-menu-line text-xl w-5 h-5 flex items-center justify-center"></i>
            </button>
          </div>
        </div>
      </nav>

      {/* Mobile full-screen menu */}
      {mobileMenuOpen && (
        <div className="fixed inset-0 z-[60] md:hidden">
          <div className="absolute inset-0 bg-black/40" onClick={() => setMobileMenuOpen(false)}></div>
          <div className="absolute top-0 right-0 bottom-0 w-[85vw] max-w-[360px] bg-background-50 shadow-2xl flex flex-col animate-slide-in-right">
            {/* Menu header */}
            <div className="flex items-center justify-between px-5 py-4 border-b border-background-200/70">
              <span className="font-heading text-lg font-semibold text-foreground-950">
                kakobuyfinds<span className="text-primary-500">.forum</span>
              </span>
              <button
                onClick={() => setMobileMenuOpen(false)}
                className="w-10 h-10 rounded-xl bg-background-100 text-foreground-700 hover:bg-primary-50 hover:text-primary-600 transition-colors flex items-center justify-center cursor-pointer"
                aria-label="Zamknij menu"
              >
                <i className="ri-close-line text-xl w-5 h-5 flex items-center justify-center"></i>
              </button>
            </div>

            {/* Menu content */}
            <div className="flex-1 overflow-y-auto px-5 py-6">
              {/* Categories section */}
              <div className="mb-8">
                <h3 className="text-xs font-semibold text-foreground-400 uppercase tracking-widest mb-4">Kategorie produktów</h3>
                <div className="grid grid-cols-2 gap-2">
                  {categories.map((cat) => (
                    <a
                      key={cat.id}
                      href={cat.url}
                      target="_blank"
                      rel="noopener nofollow"
                      onClick={() => setMobileMenuOpen(false)}
                      className="flex items-center gap-2 px-3 py-2.5 rounded-xl bg-background-100 text-foreground-700 text-xs font-medium hover:bg-primary-50 hover:text-primary-600 transition-colors whitespace-nowrap"
                    >
                      <i className="ri-shopping-bag-3-line w-3.5 h-3.5 flex items-center justify-center text-primary-400"></i>
                      {cat.name}
                    </a>
                  ))}
                </div>
              </div>

              {/* Blog section */}
              <div className="mb-8">
                <h3 className="text-xs font-semibold text-foreground-400 uppercase tracking-widest mb-4">Przewodniki</h3>
                <div className="space-y-2">
                  {blogArticles.map((article) => (
                    <Link
                      key={article.id}
                      to={`/blog/${article.id}`}
                      onClick={() => setMobileMenuOpen(false)}
                      className="block px-3 py-2.5 rounded-xl bg-background-100 text-foreground-700 text-xs font-medium hover:bg-primary-50 hover:text-primary-600 transition-colors"
                    >
                      {article.title}
                    </Link>
                  ))}
                </div>
              </div>

              {/* Quick links */}
              <div className="mb-8">
                <h3 className="text-xs font-semibold text-foreground-400 uppercase tracking-widest mb-4">Szybkie linki</h3>
                <div className="space-y-2">
                  <a
                    href="https://kaspreadsheet.com"
                    target="_blank"
                    rel="noopener nofollow"
                    onClick={() => setMobileMenuOpen(false)}
                    className="flex items-center gap-2 px-3 py-2.5 rounded-xl bg-background-100 text-foreground-700 text-xs font-medium hover:bg-primary-50 hover:text-primary-600 transition-colors"
                  >
                    <i className="ri-external-link-line w-3.5 h-3.5 flex items-center justify-center"></i>
                    kaspreadsheet.com
                  </a>
                </div>
              </div>
            </div>

            {/* Menu footer CTAs */}
            <div className="px-5 py-4 border-t border-background-200/70 space-y-3">
              <a
                href="https://kaspreadsheet.com"
                target="_blank"
                rel="noopener nofollow"
                onClick={() => setMobileMenuOpen(false)}
                className="flex items-center justify-center gap-2 w-full px-5 py-3 bg-primary-500 text-background-50 text-sm font-semibold rounded-full hover:bg-primary-600 transition-all duration-200 whitespace-nowrap cursor-pointer"
              >
                buy it now
                <i className="ri-arrow-right-up-line w-4 h-4 flex items-center justify-center"></i>
              </a>
              <a
                href="https://kaspreadsheet.com"
                target="_blank"
                rel="noopener nofollow"
                onClick={() => setMobileMenuOpen(false)}
                className="flex items-center justify-center gap-2 w-full px-5 py-3 bg-background-100 text-foreground-800 text-sm font-medium rounded-full hover:bg-background-200 transition-all duration-200 whitespace-nowrap cursor-pointer"
              >
                kakobuy sheet
                <i className="ri-arrow-right-up-line w-4 h-4 flex items-center justify-center"></i>
              </a>
            </div>
          </div>
        </div>
      )}

      {/* Hero Content */}
      <div className="relative flex-1 flex items-center min-h-screen">
        {/* Background Image */}
        <div className="absolute inset-0">
          <img
            src="https://readdy.ai/api/search-image?query=Soft%20pastel%20aesthetic%20flat%20lay%20of%20luxury%20fashion%20accessories%20including%20silk%20scarf%20gold%20jewelry%20and%20designer%20sunglasses%20arranged%20on%20a%20cream%20marble%20surface%20with%20dried%20pampas%20grass%20and%20soft%20morning%20sunlight%20creating%20gentle%20shadows%20editorial%20European%20lifestyle%20photography%20warm%20beige%20and%20blush%20pink%20tones&width=1600&height=1000&seq=hero-bg-main-v2&orientation=landscape"
            alt="Kakobuy Spreadsheet – tło"
            className="w-full h-full object-cover"
          />
          <div className="absolute inset-0 bg-gradient-to-b from-black/15 via-black/10 to-black/20"></div>
        </div>

        {/* Content */}
        <div className="relative z-10 w-full max-w-[1280px] mx-auto px-4 md:px-6">
          <div className="flex flex-col lg:flex-row items-center gap-10 lg:gap-16">
            {/* Left Column */}
            <div className="flex-1 text-center lg:text-left pt-20 md:pt-0">
              <h1 className="animate-fade-in-up text-4xl md:text-5xl lg:text-6xl font-heading font-semibold text-background-50 leading-tight">
                Twoje Źródło
                <br />
                Modowych Znalezisk
              </h1>
              <p className="animate-fade-in-up-delay-1 text-background-50/80 text-base md:text-lg mt-4 max-w-md mx-auto lg:mx-0 leading-relaxed">
                Kakobuy Spreadsheet 2026 – kompletna baza zweryfikowanych produktów z Chin. Oszczędzaj czas i pieniądze z naszym arkuszem zakupowym.
              </p>
              <div className="animate-fade-in-up-delay-2 mt-8 flex flex-col sm:flex-row items-center gap-4 justify-center lg:justify-start">
                <a
                  href="https://kaspreadsheet.com"
                  target="_blank"
                  rel="noopener nofollow"
                  className="inline-flex items-center gap-2.5 px-8 py-3.5 bg-primary-500 text-background-50 text-sm font-semibold rounded-full hover:bg-primary-600 transition-all duration-200 whitespace-nowrap cursor-pointer"
                >
                  buy it now
                  <i className="ri-arrow-right-up-line w-5 h-5 flex items-center justify-center"></i>
                </a>
                <a
                  href="https://kaspreadsheet.com"
                  target="_blank"
                  rel="noopener nofollow"
                  className="inline-flex items-center gap-2.5 px-8 py-3.5 bg-background-50/90 text-foreground-900 text-sm font-medium rounded-full hover:bg-background-50 transition-all duration-200 whitespace-nowrap cursor-pointer"
                >
                  kakobuy sheet
                  <i className="ri-arrow-right-up-line w-5 h-5 flex items-center justify-center"></i>
                </a>
              </div>
            </div>

            {/* Right Column – Category Preview */}
            <div className="flex-shrink-0 w-full lg:w-[380px] animate-fade-in-up-delay-3">
              <div className="flex items-center gap-2 mb-5">
                <span className="text-background-50/90 text-sm font-semibold">Przeglądaj</span>
                <span className="text-background-50/60 text-sm">Kategorie</span>
                <i className="ri-arrow-right-up-line text-background-50/60 w-4 h-4 flex items-center justify-center"></i>
              </div>
              <div className="grid grid-cols-2 gap-3">
                <a
                  href="https://kaspreadsheet.com/category-911-Shoes.html"
                  target="_blank"
                  rel="noopener nofollow"
                  className="relative rounded-2xl overflow-hidden h-[180px] group cursor-pointer"
                >
                  <img
                    src="https://readdy.ai/api/search-image?query=Pair%20of%20cream%20colored%20leather%20sneakers%20resting%20on%20a%20natural%20linen%20fabric%20with%20small%20white%20flowers%20and%20cotton%20branch%2C%20soft%20morning%20sunlight%20from%20a%20window%2C%20Scandinavian%20minimalist%20product%20photography%2C%20clean%20composition%20with%20negative%20space%2C%20warm%20ivory%20and%20oat%20tones&width=380&height=360&seq=hero-preview-shoes-v2&orientation=squarish"
                    alt="Buty"
                    className="w-full h-full object-cover group-hover:scale-105 transition-transform duration-500"
                  />
                  <div className="absolute bottom-0 left-0 right-0 p-3 bg-gradient-to-t from-black/50 to-transparent">
                    <span className="text-background-50 text-xs font-semibold">Buty</span>
                  </div>
                </a>
                <a
                  href="https://kaspreadsheet.com/search-912/name/Shoes.html"
                  target="_blank"
                  rel="noopener nofollow"
                  className="relative rounded-2xl overflow-hidden h-[180px] group cursor-pointer"
                >
                  <img
                    src="https://readdy.ai/api/search-image?query=Oversized%20knit%20sweater%20in%20warm%20oatmeal%20color%20carefully%20folded%20on%20a%20light%20oak%20wooden%20stool%20with%20a%20ceramic%20mug%20of%20hot%20cocoa%20beside%20it%2C%20cozy%20autumn%20morning%20light%2C%20Danish%20hygge%20aesthetic%2C%20soft%20neutral%20palette%20with%20warm%20undertones&width=380&height=360&seq=hero-preview-hoodies-v2&orientation=squarish"
                    alt="Bluzy"
                    className="w-full h-full object-cover group-hover:scale-105 transition-transform duration-500"
                  />
                  <div className="absolute bottom-0 left-0 right-0 p-3 bg-gradient-to-t from-black/50 to-transparent">
                    <span className="text-background-50 text-xs font-semibold">Bluzy</span>
                  </div>
                </a>
                <a
                  href="https://kaspreadsheet.com/search-913/name/Shoes.html"
                  target="_blank"
                  rel="noopener nofollow"
                  className="relative rounded-2xl overflow-hidden h-[180px] group cursor-pointer"
                >
                  <img
                    src="https://readdy.ai/api/search-image?query=Stack%20of%20three%20folded%20organic%20cotton%20tshirts%20in%20soft%20earth%20tones%20sage%20green%20sand%20and%20terracotta%20arranged%20on%20a%20natural%20jute%20mat%20with%20a%20small%20succulent%20plant%2C%20bright%20diffused%20daylight%2C%20Japandi%20minimalist%20style%2C%20clean%20aesthetic%20composition&width=380&height=360&seq=hero-preview-tshirts-v2&orientation=squarish"
                    alt="T-Shirty"
                    className="w-full h-full object-cover group-hover:scale-105 transition-transform duration-500"
                  />
                  <div className="absolute bottom-0 left-0 right-0 p-3 bg-gradient-to-t from-black/50 to-transparent">
                    <span className="text-background-50 text-xs font-semibold">T-Shirty</span>
                  </div>
                </a>
                <a
                  href="https://kaspreadsheet.com/search-914/name/Shoes.html"
                  target="_blank"
                  rel="noopener nofollow"
                  className="relative rounded-2xl overflow-hidden h-[180px] group cursor-pointer"
                >
                  <img
                    src="https://readdy.ai/api/search-image?query=Tan%20leather%20bomber%20jacket%20draped%20over%20a%20vintage%20brass%20coat%20rack%20against%20a%20textured%20terracotta%20colored%20wall%2C%20a%20woven%20wicker%20basket%20with%20dried%20lavender%20on%20the%20floor%2C%20golden%20late%20afternoon%20sunlight%20streaming%20through%20window%2C%20warm%20Mediterranean%20interior%20atmosphere&width=380&height=360&seq=hero-preview-jackets-v2&orientation=squarish"
                    alt="Kurtki"
                    className="w-full h-full object-cover group-hover:scale-105 transition-transform duration-500"
                  />
                  <div className="absolute bottom-0 left-0 right-0 p-3 bg-gradient-to-t from-black/50 to-transparent">
                    <span className="text-background-50 text-xs font-semibold">Kurtki</span>
                  </div>
                </a>
              </div>
            </div>
          </div>
        </div>
      </div>
    </header>
  );
}